// Aba "Catálogo de serviços/produtos" de ComercialPropostas.
//
// 04/10/2026 — todo serviço pertence a uma das 6 áreas da empresa (lista
// fechada, com busca), tem sub-área quando a área tem, e código de serviço da
// NF. "Outros" e "Estudos" não são mais opção. A aba "Em aberto" lista o que
// ainda falta arrumar, com a sugestão pronta para aplicar.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, App as AntApp, AutoComplete, Button, Empty, Flex, Grid, Input, Modal, Popconfirm, Segmented, Select, Space, Table, Tag, Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, ApartmentOutlined } from '@ant-design/icons';
import {
  AREAS_EMPRESA, OPCOES_AREA, SETORES, casaBusca, exigeSubarea, faltaNoServico, rotuloDaArea, type Subarea,
} from '../../utils/areasEmpresa';
import { CNAES, cnaeValido } from '../../utils/cnaes';
import { CamposAreaServico } from './CamposAreaServico';
import {
  aplicarAreas, atualizarServico, carregarPendencias, criarServico, desativarServico, erroDe, listarAreas, listarServicos,
  regerarCodigos, salvarSubarea,
  type AreaCatalogo, type EscolhaArea, type Pendencia, type Pendencias, type ServicoCatalogo,
} from './apiCatalogo';

const { Text } = Typography;
const UNIDADES = ['vb', 'un', 'h', 'dia', 'mês', 'km', 'ha', 'm', 'm²', 'm³', 'amostra', 'medição'];
const SEM_AREA = '__sem_area__';
const ROTULO_FALTA: Record<string, string> = { area: 'área', subarea: 'sub-área', cnae: 'CNAE' };
const descricaoCnae = (c?: string | null) => CNAES.find(x => x.codigo === c)?.descricao || '';
type Escolhas = Record<string, EscolhaArea>;

export function TagArea({ chave, texto }: { chave?: string | null; texto?: string | null }) {
  if (chave && SETORES[chave]) return <Tag color={SETORES[chave].cor} style={{ margin: 0 }}>{SETORES[chave].rotulo}</Tag>;
  return <Tag color="warning" style={{ margin: 0 }}>{texto ? `${texto} — sem área` : 'sem área'}</Tag>;
}

// ─────────────────────────── CATÁLOGO DE SERVIÇOS ───────────────────────────
export function CatalogoServicos() {
  const { message } = AntApp.useApp();
  const telaLarga = !!Grid.useBreakpoint().lg;
  const [items, setItems] = useState<ServicoCatalogo[]>([]);
  const [areas, setAreas] = useState<AreaCatalogo[]>([]);
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [pend, setPend] = useState<Pendencias | null>(null);
  const [estadoPend, setEstadoPend] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [vista, setVista] = useState<'catalogo' | 'aberto'>('catalogo');
  const [editando, setEditando] = useState<ServicoCatalogo | 'novo' | null>(null);
  const [filtroArea, setFiltroArea] = useState<string | undefined>(undefined);
  const [busca, setBusca] = useState('');
  const [modalAreas, setModalAreas] = useState(false);
  const [regenerando, setRegenerando] = useState(false);
  // O que a pessoa escolheu à mão em cada linha da aba "Em aberto". Mora AQUI
  // (e não dentro da aba) para não se perder ao trocar de aba.
  const [escolhas, setEscolhas] = useState<Escolhas>({});
  // Número do último pedido de cada lista: resposta de pedido mais antigo é jogada fora.
  const pedido = useRef(0);
  const pedidoPend = useRef(0);
  const jaCarregou = useRef(false);

  const subareas = useMemo<Subarea[]>(() => areas.flatMap(a => a.subareas || []), [areas]);

  async function carregarPend() {
    const n = ++pedidoPend.current;
    setEstadoPend('carregando');
    try {
      const d = await carregarPendencias();
      if (n !== pedidoPend.current) return;
      setPend(d); setEstadoPend('ok');
    } catch {
      if (n === pedidoPend.current) setEstadoPend('erro');     // falha NÃO vira "nada em aberto"
    }
  }
  async function carregar() {
    const n = ++pedido.current;
    try {
      const [servicos, ars] = await Promise.all([listarServicos(), listarAreas()]);
      if (n !== pedido.current) return;
      setItems(servicos); setAreas(ars); setEstado('ok');
      jaCarregou.current = true;
    } catch {
      if (n !== pedido.current) return;
      if (jaCarregou.current) {
        // Atualização depois de uma ação: o que está na tela continua (a ação já
        // foi gravada); trocar a página inteira por um erro fecharia o que a
        // pessoa está fazendo.
        message.warning('Não consegui atualizar a lista. O que você vê pode estar desatualizado — recarregue a página.', 8);
      } else {
        setEstado('erro');
      }
    }
    await carregarPend();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  async function criarSubarea(setor: string, nome: string): Promise<Subarea> {
    try {
      const nova = await salvarSubarea({ setor_chave: setor, nome });
      setAreas(as => as.map(a => (a.chave === setor ? { ...a, subareas: [...a.subareas, { ...nova, n_servicos: 0 }] } : a)));
      void carregarPend();                 // a régua da área pode ter mudado para as outras linhas
      return nova;
    } catch (e) { throw new Error(erroDe(e, 'Não consegui criar a sub-área.')); }
  }

  async function excluir(s: ServicoCatalogo) {
    try { await desativarServico(s.id); message.success(`"${s.nome}" desativado.`); await carregar(); }
    catch (e) { message.error(erroDe(e, 'Não consegui desativar. Tente de novo.'), 6); }
  }
  async function regerar() {
    setRegenerando(true);
    try {
      const d = await regerarCodigos();
      message.success(`${d.atualizados} código(s) regerado(s).`, 5);
      await carregar();
    } catch (e) { message.error(erroDe(e, 'Não consegui regerar os códigos.'), 6); }
    finally { setRegenerando(false); }
  }

  const filtrados = items.filter(i => {
    if (filtroArea === SEM_AREA && i.setor_chave) return false;
    if (filtroArea && filtroArea !== SEM_AREA && i.setor_chave !== filtroArea) return false;
    if (!busca.trim()) return true;
    return casaBusca(busca, `${i.nome} ${i.codigo || ''} ${i.descricao || ''} ${i.subarea_nome || ''} ${i.cnae || ''}`);
  });
  const semArea = items.filter(i => !i.setor_chave).length;

  if (estado === 'carregando') return <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div>;
  if (estado === 'erro') {
    return (
      <Alert type="error" showIcon message="Não consegui carregar o catálogo de serviços."
        description="Isto não quer dizer que o catálogo está vazio — só que a lista não veio. Se o sistema acabou de ser atualizado, espere um minuto e tente de novo."
        action={<Button size="small" icon={<ReloadOutlined />} onClick={() => { setEstado('carregando'); carregar(); }}>Tentar de novo</Button>} />
    );
  }

  return (
    <div>
      <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Catálogo de serviços/produtos</h2>
        <Segmented
          value={vista} onChange={(v) => setVista(v as 'catalogo' | 'aberto')}
          options={[
            { value: 'catalogo', label: 'Catálogo' },
            { value: 'aberto', label: estadoPend === 'ok' && pend ? `Em aberto (${pend.total})` : 'Em aberto' },
          ]}
        />
        <Flex gap={8} wrap="wrap" style={{ marginLeft: 'auto' }}>
          <Button icon={<ApartmentOutlined />} onClick={() => setModalAreas(true)}>Áreas e sub-áreas</Button>
          <Popconfirm title="Regerar TODOS os códigos pela área?" description="Os códigos atuais (TOP-001, HID-001…) são substituídos."
            okText="Regerar" cancelText="Manter" onConfirm={regerar}>
            <Button icon={<ReloadOutlined />} loading={regenerando}>Regerar códigos</Button>
          </Popconfirm>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditando('novo')}>Novo serviço</Button>
        </Flex>
      </Flex>

      {vista === 'catalogo' && (
        <>
          <Flex align="center" gap={10} wrap="wrap" style={{ marginBottom: 10 }}>
            <Select
              aria-label="Filtrar por área" value={filtroArea} onChange={setFiltroArea} allowClear placeholder="Todas as áreas"
              showSearch filterOption={(d, o) => casaBusca(d, o?.label)} style={{ width: 300 }}
              // "Sem área" continua na lista enquanto estiver escolhida, mesmo zerada —
              // senão o campo mostraria o valor interno no lugar do nome.
              options={[...OPCOES_AREA, ...((semArea || filtroArea === SEM_AREA) ? [{ value: SEM_AREA, label: `Sem área (${semArea})` }] : [])]}
            />
            <Input.Search aria-label="Buscar serviço" placeholder="Buscar por nome, código, sub-área…" allowClear
              value={busca} onChange={e => setBusca(e.target.value)} style={{ flex: '1 1 220px', maxWidth: 360 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{filtrados.length} de {items.length}</Text>
          </Flex>
          <Table<ServicoCatalogo>
            size="small" rowKey="id" dataSource={filtrados} pagination={false}
            // No celular o cabeçalho quebra em várias linhas e a conta da altura deixa
            // de valer: lá a tabela cresce com a página, sem rolagem dentro da rolagem.
            scroll={{ x: 900, ...(telaLarga ? { y: 'calc(100vh - 295px)' } : {}) }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum serviço encontrado." /> }}
            columns={[
              { title: 'Código', dataIndex: 'codigo', width: 96, render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 11, color: '#6A7092' }}>{v || '—'}</Text> },
              { title: 'Nome', dataIndex: 'nome', render: (v) => <Text strong style={{ color: '#020D7F', fontSize: 12 }}>{v}</Text> },
              { title: 'Área', key: 'area', width: 270, render: (_, s) => <TagArea chave={s.setor_chave} texto={s.area} /> },
              {
                title: 'Sub-área', key: 'sub', width: 190,
                render: (_, s) => s.subarea_nome
                  ? <span style={{ fontSize: 12 }}>{s.subarea_nome}</span>
                  : (exigeSubarea(subareas, s.setor_chave) ? <Tag color="warning" style={{ margin: 0 }}>falta</Tag> : <Text type="secondary">—</Text>),
              },
              {
                title: 'CNAE', dataIndex: 'cnae', width: 110,
                render: (v) => {
                  if (!v) return <Tag color="warning" style={{ margin: 0 }}>falta</Tag>;
                  if (!cnaeValido(v)) return <Tag color="warning" style={{ margin: 0 }} title="Este código não está na lista de códigos de serviço da NF.">{v} ?</Tag>;
                  return <span title={descricaoCnae(v)} style={{ fontSize: 12 }}>{v}</span>;
                },
              },
              { title: 'Unid.', dataIndex: 'unidade', width: 70, render: (v) => v || 'vb' },
              {
                title: '', key: 'acoes', width: 92, align: 'right',
                render: (_, s) => (
                  <Space size={2}>
                    <Button size="small" type="text" icon={<EditOutlined />} aria-label={`Editar ${s.nome}`} onClick={() => setEditando(s)} />
                    <Popconfirm title="Desativar este serviço?" description="Ele some do catálogo e das novas propostas."
                      okText="Desativar" cancelText="Manter" onConfirm={() => excluir(s)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Desativar ${s.nome}`} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </>
      )}

      {vista === 'aberto' && (
        <EmAberto pend={pend} estado={estadoPend} subareas={subareas} onCriarSubarea={criarSubarea}
          escolhas={escolhas} setEscolhas={setEscolhas} telaLarga={telaLarga}
          onRecarregar={carregarPend} onAplicado={carregar} />
      )}

      {modalAreas && (
        <ModalAreas areas={areas} onClose={() => setModalAreas(false)} onAlterado={carregar} />
      )}
      {editando && (
        <FormServico servico={editando === 'novo' ? null : editando} subareas={subareas} onCriarSubarea={criarSubarea}
          onClose={() => setEditando(null)} onSaved={async () => { setEditando(null); await carregar(); }} />
      )}
    </div>
  );
}

// ─────────────────────────── EM ABERTO ───────────────────────────
function EmAberto({ pend, estado, subareas, onCriarSubarea, escolhas, setEscolhas, telaLarga, onRecarregar, onAplicado }: {
  pend: Pendencias | null; estado: 'carregando' | 'ok' | 'erro'; subareas: Subarea[];
  onCriarSubarea: (setor: string, nome: string) => Promise<Subarea>;
  escolhas: Escolhas; setEscolhas: React.Dispatch<React.SetStateAction<Escolhas>>; telaLarga: boolean;
  onRecarregar: () => void; onAplicado: () => Promise<void>;
}) {
  const { message } = AntApp.useApp();
  const [aplicando, setAplicando] = useState<string | null>(null);   // id da linha, ou 'todas'

  if (estado === 'carregando' && !pend) return <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div>;
  if (estado === 'erro' || !pend) {
    return (
      <Alert type="error" showIcon message="Não consegui carregar a lista do que está em aberto."
        description="Isto não quer dizer que está tudo certo — só que a lista não veio."
        action={<Button size="small" icon={<ReloadOutlined />} onClick={onRecarregar}>Tentar de novo</Button>} />
    );
  }
  if (!pend.itens.length) {
    return <Alert type="success" showIcon message="Nada em aberto." description="Todo serviço ativo tem área, sub-área (quando a área tem) e código de serviço da NF." />;
  }

  // Quem não mexeu na linha fica com a sugestão do servidor.
  const escolhaDe = (p: Pendencia): EscolhaArea => escolhas[p.id] || p.sugestao;
  const pronta = (p: Pendencia) => faltaNoServico({ nome: p.nome, ...escolhaDe(p) }, subareas, p.subarea_id) === null;
  // "Aplicar todas" só leva as sugestões que o servidor deu inteiras, que a
  // pessoa NÃO alterou e que continuam valendo com as sub-áreas de AGORA (uma
  // sub-área criada ou desativada há um minuto muda a régua) — o que ela
  // escolheu à mão ela aplica na linha.
  const sugeridasProntas = pend.itens.filter(p => p.completa && !escolhas[p.id] && pronta(p));
  const resumo = [
    pend.sem_area ? `${pend.sem_area} sem área` : '',
    pend.sem_subarea ? `${pend.sem_subarea} sem sub-área` : '',
    pend.sem_cnae ? `${pend.sem_cnae} sem CNAE` : '',
  ].filter(Boolean).join(' · ');

  async function aplicar(lista: Pendencia[], quem: string) {
    if (aplicando) return;
    setAplicando(quem);
    try {
      const r = await aplicarAreas(lista.map(p => ({ id: p.id, ...escolhaDe(p) })));
      message.success(r.aplicados === 1 ? 'Serviço atualizado.' : `${r.aplicados} serviços atualizados.`, 5);
      // Só as linhas aplicadas saem: o que a pessoa já escolheu nas outras fica.
      setEscolhas(e => { const n = { ...e }; lista.forEach(p => delete n[p.id]); return n; });
      await onAplicado();
    } catch (e) {
      message.error(erroDe(e, 'Não consegui aplicar. Confira as linhas e tente de novo.'), 8);
      onRecarregar();                       // a lista pode ter mudado por baixo
    } finally { setAplicando(null); }
  }

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 10 }}
        message={
          // O botão vai DENTRO do texto, com quebra: no celular ele desce para a
          // linha de baixo em vez de espremer a explicação numa coluna estreita.
          <Flex gap={12} wrap="wrap" align="center" justify="space-between">
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <div><strong>{resumo}</strong></div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                A sugestão já vem escolhida em cada linha: confira, troque se precisar e aplique. Serviço sem sugestão de área é você quem escolhe.
              </div>
            </div>
            {sugeridasProntas.length > 0 && (
              <Popconfirm
                title={`Aplicar as ${sugeridasProntas.length} sugestões prontas?`}
                description="Só as linhas com sugestão completa e que você não alterou. As outras continuam na lista."
                okText="Aplicar" cancelText="Agora não" onConfirm={() => aplicar(sugeridasProntas, 'todas')}>
                <Button type="primary" loading={aplicando === 'todas'} disabled={!!aplicando}>
                  Aplicar as {sugeridasProntas.length} sugestões prontas
                </Button>
              </Popconfirm>
            )}
          </Flex>
        }
      />
      <Table<Pendencia>
        size="small" rowKey="id" dataSource={pend.itens} pagination={false}
        scroll={{ x: 1190, ...(telaLarga ? { y: 'calc(100vh - 355px)' } : {}) }}
        columns={[
          {
            title: 'Serviço', key: 'servico',
            render: (_, p) => (
              <div>
                <Text strong style={{ fontSize: 12, color: '#020D7F' }}>{p.nome}</Text>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {p.codigo || 'sem código'} · hoje: {p.setor_chave ? rotuloDaArea(p.setor_chave) : (p.area ? `"${p.area}"` : 'sem área')}
                </div>
              </div>
            ),
          },
          {
            title: 'Falta', key: 'falta', width: 140,
            render: (_, p) => <Space size={4} wrap>{p.falta.map(f => <Tag key={f} color="warning" style={{ margin: 0 }}>{ROTULO_FALTA[f]}</Tag>)}</Space>,
          },
          {
            title: 'Área · Sub-área · CNAE', key: 'escolha', width: 750,
            render: (_, p) => (
              <Flex gap={6}>
                <CamposAreaServico
                  tamanho="small" valor={escolhaDe(p)} subareas={subareas} onCriarSubarea={onCriarSubarea}
                  manterSubarea={p.subarea_id} desabilitado={!!aplicando}
                  larguras={{ area: 320, subarea: 250, cnae: 150 }}
                  onChange={(v) => setEscolhas(e => ({ ...e, [p.id]: v }))}
                />
              </Flex>
            ),
          },
          {
            title: '', key: 'aplicar', width: 96, align: 'right',
            render: (_, p) => (
              <Button size="small" type={pronta(p) ? 'primary' : 'default'} disabled={!pronta(p) || !!aplicando}
                loading={aplicando === p.id} onClick={() => aplicar([p], p.id)}>
                Aplicar
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}

// ─────────────────────────── ÁREAS E SUB-ÁREAS ───────────────────────────
function ModalAreas({ areas, onClose, onAlterado }: {
  areas: AreaCatalogo[]; onClose: () => void; onAlterado: () => Promise<void>;
}) {
  const { message } = AntApp.useApp();
  const [novo, setNovo] = useState<Record<string, string>>({});          // nome digitado por área
  const [renomeando, setRenomeando] = useState<{ id: string; nome: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function salvar(chaveOcupado: string, dados: { id?: string; setor_chave?: string; nome: string; ativo?: boolean }, okMsg: string) {
    if (ocupado) return false;                       // Enter repetido não manda duas vezes
    setOcupado(chaveOcupado);
    try {
      await salvarSubarea(dados);
      message.success(okMsg, 4);
      await onAlterado();
      return true;
    } catch (e) {
      message.error(erroDe(e, 'Não consegui salvar a sub-área.'), 8);
      return false;
    } finally { setOcupado(null); }
  }
  async function criar(chave: string) {
    const nome = (novo[chave] || '').trim();
    if (nome && await salvar(`novo-${chave}`, { setor_chave: chave, nome }, 'Sub-área criada.')) setNovo(n => ({ ...n, [chave]: '' }));
  }
  async function renomear(s: Subarea) {
    const nome = (renomeando?.nome || '').trim();
    if (nome && await salvar(s.id, { id: s.id, nome }, 'Sub-área renomeada.')) setRenomeando(null);
  }

  const porChave = new Map(areas.map(a => [a.chave, a]));

  return (
    <Modal open onCancel={onClose} title="Áreas e sub-áreas" width="94vw" style={{ maxWidth: 760, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      footer={<Button onClick={onClose}>Fechar</Button>}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        As áreas são as 6 da empresa e não mudam por aqui. Dentro de cada uma você cria, renomeia e desativa
        sub-áreas. Quando a área tem sub-área ativa, o serviço é obrigado a escolher uma.
      </Text>
      {AREAS_EMPRESA.filter(a => porChave.has(a.chave)).map(a => {
        const area = porChave.get(a.chave)!;
        // A primeira sub-área ATIVA de uma área muda a regra da área inteira.
        const primeira = !area.subareas.some(s => s.ativo);
        const avisoPrimeira = {
          title: `${a.rotulo} passa a exigir sub-área`,
          description: `Os ${area.n_servicos} serviço(s) desta área vão aparecer em "Em aberto" até alguém escolher a sub-área de cada um.`,
          cancelText: 'Agora não',
        };
        return (
          <div key={a.chave} style={{ border: '1px solid #F0F1F5', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
            <Flex justify="space-between" align="center" gap={8} wrap="wrap">
              <Space size={8}>
                <span style={{ width: 10, height: 10, borderRadius: 5, background: a.cor, display: 'inline-block' }} />
                <Text strong>{a.rotulo}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>{area.n_servicos} serviço(s)</Text>
            </Flex>
            {area.subareas.length === 0 && <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>Sem sub-áreas — o serviço fica só com a área.</Text>}
            {area.subareas.map(s => (
              <Flex key={s.id} justify="space-between" align="center" gap={8} wrap="wrap"
                style={{ padding: '6px 0 0 18px', opacity: s.ativo ? 1 : 0.55 }}>
                {renomeando?.id === s.id ? (
                  <Space.Compact style={{ flex: '1 1 220px' }}>
                    <Input size="small" value={renomeando.nome} maxLength={80} autoFocus aria-label="Novo nome da sub-área"
                      onChange={e => setRenomeando({ id: s.id, nome: e.target.value })}
                      onPressEnter={() => renomear(s)} />
                    <Button size="small" type="primary" loading={ocupado === s.id} disabled={!renomeando.nome.trim()}
                      onClick={() => renomear(s)}>Salvar</Button>
                    <Button size="small" onClick={() => setRenomeando(null)}>Cancelar</Button>
                  </Space.Compact>
                ) : (
                  <span style={{ fontSize: 13 }}>
                    {s.nome} {!s.ativo && <Tag style={{ marginLeft: 6 }}>desativada</Tag>}
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{s.n_servicos ?? 0} serviço(s)</Text>
                  </span>
                )}
                {renomeando?.id !== s.id && (
                  <Space size={4}>
                    <Button size="small" type="text" onClick={() => setRenomeando({ id: s.id, nome: s.nome })}>Renomear</Button>
                    {s.ativo ? (
                      <Popconfirm title="Desativar esta sub-área?"
                        description="Ela some das opções. Os serviços que já a usam continuam com ela."
                        okText="Desativar" cancelText="Manter"
                        onConfirm={() => salvar(s.id, { id: s.id, nome: s.nome, ativo: false }, 'Sub-área desativada.')}>
                        <Button size="small" type="text" danger loading={ocupado === s.id}>Desativar</Button>
                      </Popconfirm>
                    ) : primeira ? (
                      <Popconfirm {...avisoPrimeira} okText="Reativar"
                        onConfirm={() => salvar(s.id, { id: s.id, nome: s.nome, ativo: true }, 'Sub-área reativada.')}>
                        <Button size="small" type="text" loading={ocupado === s.id}>Reativar</Button>
                      </Popconfirm>
                    ) : (
                      <Button size="small" type="text" loading={ocupado === s.id}
                        onClick={() => salvar(s.id, { id: s.id, nome: s.nome, ativo: true }, 'Sub-área reativada.')}>Reativar</Button>
                    )}
                  </Space>
                )}
              </Flex>
            ))}
            <Space.Compact style={{ marginTop: 8, paddingLeft: 18, width: '100%', maxWidth: 420 }}>
              <Input size="small" placeholder={`Nova sub-área de ${a.rotulo}`} maxLength={80} aria-label={`Nova sub-área de ${a.rotulo}`}
                value={novo[a.chave] || ''} onChange={e => setNovo(n => ({ ...n, [a.chave]: e.target.value }))}
                // Na primeira sub-área o Enter não cria direto: passa pelo aviso do botão.
                onPressEnter={() => { if (!primeira) criar(a.chave); }} />
              {primeira ? (
                <Popconfirm {...avisoPrimeira} okText="Criar" onConfirm={() => criar(a.chave)}
                  disabled={!(novo[a.chave] || '').trim()}>
                  <Button size="small" icon={<PlusOutlined />} loading={ocupado === `novo-${a.chave}`} disabled={!(novo[a.chave] || '').trim()}>Criar</Button>
                </Popconfirm>
              ) : (
                <Button size="small" icon={<PlusOutlined />} loading={ocupado === `novo-${a.chave}`} disabled={!(novo[a.chave] || '').trim()}
                  onClick={() => criar(a.chave)}>Criar</Button>
              )}
            </Space.Compact>
          </div>
        );
      })}
    </Modal>
  );
}

// ─────────────────────────── CADASTRO DO SERVIÇO ───────────────────────────
function FormServico({ servico, subareas, onCriarSubarea, onClose, onSaved }: {
  servico: ServicoCatalogo | null; subareas: Subarea[];
  onCriarSubarea: (setor: string, nome: string) => Promise<Subarea>;
  onClose: () => void; onSaved: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form, setForm] = useState({
    codigo: servico?.codigo || '',
    nome: servico?.nome || '',
    unidade: servico?.unidade || 'vb',
    descricao: servico?.descricao || '',
  });
  const [escolha, setEscolha] = useState<EscolhaArea>({
    setor_chave: servico?.setor_chave || null,
    subarea_id: servico?.subarea_id || null,
    cnae: servico?.cnae || null,
  });
  const [salvando, setSalvando] = useState(false);
  const falta = faltaNoServico({ nome: form.nome, ...escolha }, subareas, servico?.subarea_id);

  async function salvar() {
    if (salvando) return;
    if (falta) { message.warning(falta, 5); return; }
    setSalvando(true);
    try {
      const dados = { codigo: form.codigo.trim() || null, nome: form.nome.trim(), unidade: form.unidade.trim() || 'vb', descricao: form.descricao || null, ...escolha };
      if (servico) await atualizarServico(servico.id, dados); else await criarServico(dados);
      message.success(servico ? 'Serviço atualizado.' : 'Serviço criado.', 4);
      onSaved();
    } catch (e) {
      message.error(erroDe(e, 'Não consegui salvar o serviço. Tente de novo.'), 8);
    } finally { setSalvando(false); }
  }

  const rotulo = (t: string) => <div style={{ fontSize: 12, color: '#555', marginBottom: 4, fontWeight: 600 }}>{t}</div>;

  return (
    <Modal open onCancel={onClose} title={servico ? 'Editar serviço' : 'Novo serviço/produto'}
      width="94vw" style={{ maxWidth: 720, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      footer={
        <Flex justify="space-between" align="center" gap={12} wrap="wrap">
          <Text type={falta ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>{falta || 'Pronto para salvar.'}</Text>
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" loading={salvando} disabled={!!falta} onClick={salvar}>Salvar</Button>
          </Space>
        </Flex>
      }>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          {rotulo('Nome *')}
          <Input aria-label="Nome do serviço" value={form.nome} maxLength={200} autoFocus
            placeholder="Ex.: Levantamento planialtimétrico" onChange={e => setForm({ ...form, nome: e.target.value })} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          {rotulo('Área * · Sub-área · Código de serviço da NF (CNAE) *')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
            <CamposAreaServico valor={escolha} onChange={setEscolha} subareas={subareas}
              onCriarSubarea={onCriarSubarea} manterSubarea={servico?.subarea_id} />
          </div>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            A área define o setor da receita quando o serviço é vendido. O código da NF já vem com o padrão da área — troque se este serviço for diferente
            (ex.: batimetria dentro de Topografia usa o código de batimetria/hidrometria).
          </Text>
        </div>
        <div>
          {rotulo('Código')}
          <Input aria-label="Código do serviço" value={form.codigo} maxLength={40}
            placeholder="Em branco, o sistema gera (TOP-001…)" onChange={e => setForm({ ...form, codigo: e.target.value })} />
        </div>
        <div>
          {rotulo('Unidade')}
          <AutoComplete aria-label="Unidade" value={form.unidade} style={{ width: '100%' }}
            options={UNIDADES.map(u => ({ value: u }))}
            filterOption={(d, o) => casaBusca(d, o?.value)}
            onChange={(v) => setForm({ ...form, unidade: v })} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          {rotulo('Descrição (aparece pré-preenchida na proposta ao selecionar este serviço)')}
          <Input.TextArea aria-label="Descrição" value={form.descricao} autoSize={{ minRows: 3, maxRows: 10 }}
            onChange={e => setForm({ ...form, descricao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}
